/**
 * Learn F1 — diagram library.
 *
 * Purpose-built, lightweight SVG diagrams. Each explains one specific concept;
 * topics reference them via `visual.type`. Topics with no matching diagram show
 * no visual at all — no generic filler.
 */
import React from "react";

const INK = "#e4e4e7";
const DIM = "#71717a";
const FAINT = "#3f3f46";
const RED = "#e10600";
const PANEL = "#18181b";

function Frame({ children, viewBox = "0 0 640 300", label }) {
    return (
        <svg viewBox={viewBox} className="h-auto w-full" role="img" aria-label={label}>
            {children}
        </svg>
    );
}

const mono = { fontFamily: "ui-monospace, SFMono-Regular, monospace" };

function Label({ x, y, children, size = 11, fill = DIM, anchor = "start", weight = 400 }) {
    return (
        <text x={x} y={y} fontSize={size} fill={fill} textAnchor={anchor} fontWeight={weight} style={mono}>
            {children}
        </text>
    );
}

/* ---------------------------------- Getting started ---------------------------------- */

export function WeekendTimeline() {
    const days = [
        { day: "FRI", items: ["FP1", "FP2"] },
        { day: "SAT", items: ["FP3", "QUALI"] },
        { day: "SUN", items: ["RACE"] },
    ];
    return (
        <Frame viewBox="0 0 640 190" label="Race weekend timeline: practice Friday, qualifying Saturday, race Sunday">
            <line x1="40" y1="150" x2="600" y2="150" stroke={FAINT} strokeWidth="2" />
            {days.map((d, i) => {
                const x = 60 + i * 190;
                return (
                    <g key={d.day}>
                        <Label x={x} y={175} fill={INK} weight={600}>{d.day}</Label>
                        {d.items.map((item, j) => {
                            const points = item === "RACE";
                            const quali = item === "QUALI";
                            const bx = x + j * 88;
                            return (
                                <g key={item}>
                                    <rect x={bx} y={70} width={76} height={44} rx={6} fill={PANEL} stroke={points ? RED : quali ? INK : FAINT} strokeWidth={points ? 2 : 1} />
                                    <Label x={bx + 38} y={96} anchor="middle" fill={points ? RED : INK} weight={600}>{item}</Label>
                                    <line x1={bx + 38} y1={114} x2={bx + 38} y2={148} stroke={FAINT} strokeDasharray="3 3" />
                                </g>
                            );
                        })}
                    </g>
                );
            })}
            <Label x={60} y={40} fill={DIM}>learning the track · setup</Label>
            <Label x={250} y={40} fill={DIM}>sets the grid</Label>
            <Label x={440} y={40} fill={RED}>points paid here</Label>
        </Frame>
    );
}

export function SprintTimeline() {
    const blocks = [
        { day: "FRI", items: [["FP1", false], ["SPRINT QUALI", false]] },
        { day: "SAT", items: [["SPRINT", true], ["QUALI", false]] },
        { day: "SUN", items: [["RACE", true]] },
    ];
    return (
        <Frame viewBox="0 0 640 190" label="Sprint weekend timeline: one practice, sprint Saturday, race Sunday">
            <line x1="40" y1="150" x2="600" y2="150" stroke={FAINT} strokeWidth="2" />
            {blocks.map((d, i) => {
                const x = 50 + i * 200;
                return (
                    <g key={d.day}>
                        <Label x={x} y={175} fill={INK} weight={600}>{d.day}</Label>
                        {d.items.map(([item, points], j) => {
                            const bx = x + j * 96;
                            return (
                                <g key={item}>
                                    <rect x={bx} y={70} width={90} height={44} rx={6} fill={PANEL} stroke={points ? RED : FAINT} strokeWidth={points ? 2 : 1} />
                                    <Label x={bx + 45} y={96} anchor="middle" size={item.length > 8 ? 9 : 11} fill={points ? RED : INK} weight={600}>{item}</Label>
                                </g>
                            );
                        })}
                    </g>
                );
            })}
            <Label x={50} y={40} fill={DIM}>one practice session only</Label>
            <Label x={320} y={40} fill={RED}>both red blocks pay points</Label>
        </Frame>
    );
}

export function QualifyingFlow() {
    const stages = [
        { name: "Q1", time: "18 min", cars: "20 cars", out: "5 out → P16–20" },
        { name: "Q2", time: "15 min", cars: "15 cars", out: "5 out → P11–15" },
        { name: "Q3", time: "12 min", cars: "10 cars", out: "shootout → pole" },
    ];
    return (
        <Frame viewBox="0 0 640 200" label="Qualifying knockout flow: Q1 to Q2 to Q3">
            {stages.map((s, i) => {
                const x = 30 + i * 210;
                const last = i === 2;
                return (
                    <g key={s.name}>
                        <rect x={x} y={50} width={170} height={100} rx={8} fill={PANEL} stroke={last ? RED : FAINT} strokeWidth={last ? 2 : 1} />
                        <Label x={x + 16} y={80} size={18} fill={last ? RED : INK} weight={700}>{s.name}</Label>
                        <Label x={x + 16} y={104}>{s.time} · {s.cars}</Label>
                        <Label x={x + 16} y={128} fill={last ? INK : DIM}>{s.out}</Label>
                        {i < 2 ? <path d={`M ${x + 178} 100 l 22 0 m -8 -6 l 8 6 l -8 6`} stroke={INK} strokeWidth="1.5" fill="none" /> : null}
                    </g>
                );
            })}
            <Label x={30} y={180} fill={DIM}>slowest five eliminated at each stage — final runs land on the grippiest track</Label>
        </Frame>
    );
}

export function TimingScreen() {
    const rows = [
        ["P1", "VER", "LEADER", "1:31.2", "soft · 12"],
        ["P2", "NOR", "+1.8", "1:31.0", "medium · 8"],
        ["P3", "LEC", "+4.1", "1:31.5", "soft · 14"],
        ["P4", "HAM", "+22.9", "1:31.9", "hard · 20"],
    ];
    return (
        <Frame viewBox="0 0 640 210" label="Timing screen anatomy: position, gap, last lap, tire">
            <Label x={40} y={32} fill={DIM}>POS</Label>
            <Label x={110} y={32} fill={DIM}>DRIVER</Label>
            <Label x={220} y={32} fill={DIM}>GAP</Label>
            <Label x={340} y={32} fill={DIM}>LAST LAP</Label>
            <Label x={470} y={32} fill={DIM}>TIRE · AGE</Label>
            {rows.map((r, i) => {
                const y = 62 + i * 34;
                const battle = i === 1;
                return (
                    <g key={r[1]}>
                        {battle ? <rect x={28} y={y - 20} width={584} height={28} rx={4} fill={RED} opacity="0.08" /> : null}
                        <Label x={40} y={y} fill={INK} weight={600}>{r[0]}</Label>
                        <Label x={110} y={y} fill={INK} weight={600}>{r[1]}</Label>
                        <Label x={220} y={y} fill={battle ? RED : INK}>{r[2]}</Label>
                        <Label x={340} y={y} fill={battle ? "#a78bfa" : INK}>{r[3]}</Label>
                        <Label x={470} y={y} fill={DIM}>{r[4]}</Label>
                    </g>
                );
            })}
            <Label x={40} y={196} fill={RED}>NOR laps 0.2s faster on younger tires → catches the leader in ~9 laps</Label>
        </Frame>
    );
}

/* ---------------------------------- Rules ---------------------------------- */

export function FlagCards() {
    const flags = [
        { name: "GREEN", color: "#16a34a", text: "track clear — racing" },
        { name: "YELLOW", color: "#eab308", text: "hazard — slow, no overtaking" },
        { name: "RED", color: RED, text: "session stopped" },
        { name: "BLUE", color: "#3b82f6", text: "faster car behind — let it lap" },
        { name: "BLACK / WHITE", color: INK, text: "warning for conduct" },
        { name: "CHEQUERED", color: INK, text: "session over" },
    ];
    return (
        <Frame viewBox="0 0 640 260" label="Flag meanings">
            {flags.map((f, i) => {
                const x = 30 + (i % 2) * 300;
                const y = 20 + Math.floor(i / 2) * 76;
                return (
                    <g key={f.name}>
                        <rect x={x} y={y} width={44} height={30} rx={3} fill={f.name === "CHEQUERED" ? "url(#cheq)" : f.color} opacity={f.name === "BLACK / WHITE" ? 1 : 0.9} />
                        {f.name === "BLACK / WHITE" ? (
                            <>
                                <rect x={x} y={y} width={22} height={30} fill="#09090b" stroke={FAINT} />
                                <rect x={x + 22} y={y} width={22} height={30} fill={INK} />
                            </>
                        ) : null}
                        <Label x={x + 58} y={y + 14} fill={INK} weight={600}>{f.name}</Label>
                        <Label x={x + 58} y={y + 30} size={10}>{f.text}</Label>
                    </g>
                );
            })}
            <defs>
                <pattern id="cheq" width="12" height="12" patternUnits="userSpaceOnUse">
                    <rect width="6" height="6" fill={INK} />
                    <rect x="6" y="6" width="6" height="6" fill={INK} />
                    <rect x="6" width="6" height="6" fill="#09090b" />
                    <rect y="6" width="6" height="6" fill="#09090b" />
                </pattern>
            </defs>
        </Frame>
    );
}

export function SafetyCarRestart() {
    return (
        <Frame viewBox="0 0 640 210" label="Safety car: field bunches, gaps erased, restart queue">
            <Label x={30} y={30} fill={DIM}>BEFORE — gaps earned on track</Label>
            <line x1="30" y1="60" x2="610" y2="60" stroke={FAINT} />
            {[60, 200, 260, 420, 560].map((x, i) => (
                <circle key={`b${i}`} cx={x} cy={60} r={9} fill={i === 0 ? RED : PANEL} stroke={INK} />
            ))}
            <Label x={30} y={120} fill={DIM}>UNDER SAFETY CAR — everyone nose-to-tail</Label>
            <line x1="30" y1="150" x2="610" y2="150" stroke={FAINT} />
            <rect x={44} y={140} width={26} height={20} rx={4} fill={INK} />
            <Label x={57} y={177} anchor="middle" size={9}>SC</Label>
            {[100, 130, 160, 190, 220].map((x, i) => (
                <circle key={`a${i}`} cx={x} cy={150} r={9} fill={i === 0 ? RED : PANEL} stroke={INK} />
            ))}
            <Label x={280} y={155} fill={RED}>30s lead → 0s. Pit stops cost half as much.</Label>
        </Frame>
    );
}

export function DrsZones() {
    return (
        <Frame viewBox="0 0 640 240" label="DRS detection point and activation zone on a track segment">
            <path d="M 60 190 C 140 190 170 80 280 70 L 500 55 C 570 52 600 90 590 130" fill="none" stroke={FAINT} strokeWidth="22" strokeLinecap="round" />
            <path d="M 60 190 C 140 190 170 80 280 70 L 500 55" fill="none" stroke="none" />
            <line x1="255" y1="40" x2="255" y2="105" stroke={INK} strokeDasharray="4 4" />
            <Label x={255} y={30} anchor="middle" fill={INK} weight={600}>DETECTION</Label>
            <Label x={255} y={125} anchor="middle" size={10}>gap ≤ 1.0s measured here</Label>
            <path d="M 300 69 L 495 56" stroke={RED} strokeWidth="22" strokeLinecap="round" opacity="0.85" />
            <Label x={400} y={35} anchor="middle" fill={RED} weight={600}>ACTIVATION ZONE</Label>
            <Label x={400} y={100} anchor="middle" size={10}>wing may open → +10–15 km/h</Label>
            <circle cx="150" cy="145" r="8" fill={PANEL} stroke={INK} />
            <circle cx="185" cy="122" r="8" fill={RED} />
            <Label x={120} y={170} size={10}>chaser within 1s at detection…</Label>
            <Label x={430} y={135} size={10} fill={INK}>…attacks on the straight</Label>
        </Frame>
    );
}

/* ---------------------------------- Strategy ---------------------------------- */

export function CompoundLadder() {
    const rows = [
        { name: "SOFT", color: RED, pace: 200, life: 90, note: "fastest · shortest" },
        { name: "MEDIUM", color: "#eab308", pace: 160, life: 150, note: "the compromise" },
        { name: "HARD", color: INK, pace: 120, life: 220, note: "slowest · longest" },
    ];
    return (
        <Frame viewBox="0 0 640 230" label="Compound trade-off: pace versus stint life">
            <Label x={150} y={30} fill={DIM}>PACE</Label>
            <Label x={420} y={30} fill={DIM}>STINT LIFE</Label>
            {rows.map((r, i) => {
                const y = 60 + i * 56;
                return (
                    <g key={r.name}>
                        <circle cx={60} cy={y + 8} r={13} fill="none" stroke={r.color} strokeWidth="3.5" />
                        <Label x={90} y={y + 13} fill={INK} weight={600}>{r.name}</Label>
                        <rect x={150} y={y} width={r.pace} height={16} rx={3} fill={r.color} opacity="0.75" />
                        <rect x={420} y={y} width={r.life * 0.8} height={16} rx={3} fill={FAINT} />
                        <Label x={420 + r.life * 0.8 + 8} y={y + 13} size={10}>{r.note}</Label>
                    </g>
                );
            })}
            <Label x={60} y={212} fill={DIM}>dry races must use two different slick compounds — at least one stop is guaranteed</Label>
        </Frame>
    );
}

export function DegCurve() {
    return (
        <Frame viewBox="0 0 640 240" label="Tire degradation curve with cliff">
            <line x1="60" y1="190" x2="600" y2="190" stroke={DIM} />
            <line x1="60" y1="190" x2="60" y2="30" stroke={DIM} />
            <Label x={330} y={218} anchor="middle">laps into stint →</Label>
            <text x={30} y={110} fontSize={11} fill={DIM} transform="rotate(-90 30 110)" textAnchor="middle" style={mono}>lap time →</text>
            <path d="M 70 60 C 200 66 320 80 430 100" stroke={INK} strokeWidth="2.5" fill="none" />
            <path d="M 430 100 C 470 110 490 150 505 185" stroke={RED} strokeWidth="2.5" fill="none" />
            <line x1="430" y1="40" x2="430" y2="190" stroke={RED} strokeDasharray="4 4" opacity="0.6" />
            <Label x={200} y={50} fill={INK}>steady deg: hundredths per lap</Label>
            <Label x={440} y={60} fill={RED} weight={600}>the cliff</Label>
            <Label x={440} y={78} size={10} fill={RED}>seconds per lap, almost at once</Label>
            <Label x={70} y={210} size={10} fill={DIM}>pit before the cliff, not after it finds you</Label>
        </Frame>
    );
}

export function UndercutTimeline() {
    return (
        <Frame viewBox="0 0 640 250" label="Undercut sequence across three laps">
            {["LAP 20", "LAP 21", "LAP 22"].map((lap, i) => (
                <Label key={lap} x={30} y={50 + i * 64} fill={DIM}>{lap}</Label>
            ))}
            {/* Lap 20: B pits from behind */}
            <line x1="110" y1="46" x2="610" y2="46" stroke={FAINT} />
            <circle cx="380" cy="46" r="9" fill={PANEL} stroke={INK} />
            <Label x={380} y={28} anchor="middle" size={10}>A stays out (worn)</Label>
            <circle cx="320" cy="46" r="9" fill={RED} />
            <Label x={300} y={70} size={10} fill={RED}>B boxes ↓</Label>
            {/* Lap 21: B on fresh tires, fast outlap */}
            <line x1="110" y1="110" x2="610" y2="110" stroke={FAINT} />
            <circle cx="392" cy="110" r="9" fill={PANEL} stroke={INK} />
            <circle cx="332" cy="110" r="9" fill={RED} />
            <path d="M 332 128 l 40 0 m -8 -5 l 8 5 l -8 5" stroke={RED} strokeWidth="1.5" fill="none" />
            <Label x={140} y={132} size={10} fill={RED}>fresh-tire outlap: −2.5s</Label>
            {/* Lap 22: A pits, comes out behind */}
            <line x1="110" y1="174" x2="610" y2="174" stroke={FAINT} />
            <circle cx="420" cy="174" r="9" fill={RED} />
            <circle cx="470" cy="174" r="9" fill={PANEL} stroke={INK} />
            <Label x={430} y={200} size={10}>A exits the pits… behind B</Label>
            <Label x={110} y={232} size={10} fill={DIM}>position changed hands without an on-track pass — that's the undercut</Label>
        </Frame>
    );
}

export function PitLoss() {
    return (
        <Frame viewBox="0 0 640 220" label="Anatomy of pit loss: entry, limiter, stop, exit">
            <line x1="40" y1="90" x2="600" y2="90" stroke={FAINT} strokeWidth="16" strokeLinecap="round" />
            <path d="M 150 90 C 200 90 210 140 260 140 L 400 140 C 450 140 460 90 505 90" stroke={PANEL} strokeWidth="14" fill="none" strokeLinecap="round" />
            <path d="M 150 90 C 200 90 210 140 260 140 L 400 140 C 450 140 460 90 505 90" stroke={RED} strokeWidth="2" fill="none" strokeDasharray="5 4" />
            <rect x={310} y={128} width={40} height={24} rx={4} fill={PANEL} stroke={RED} strokeWidth="1.5" />
            <Label x={330} y={144} anchor="middle" size={10} fill={RED} weight={600}>2.5s</Label>
            <Label x={330} y={176} anchor="middle" size={10}>stationary</Label>
            <Label x={225} y={176} anchor="middle" size={10}>80 km/h limiter</Label>
            <Label x={70} y={60} size={10} fill={DIM}>rivals at full speed up here</Label>
            <Label x={330} y={40} anchor="middle" fill={INK} weight={600}>total pit loss ≈ 18–28s depending on circuit</Label>
        </Frame>
    );
}

/* ---------------------------------- Racecraft ---------------------------------- */

export function RacingLine() {
    return (
        <Frame viewBox="0 0 640 260" label="Racing line through a corner: wide entry, apex, wide exit">
            <path d="M 80 230 L 420 230 C 520 230 560 190 560 110 L 560 30" fill="none" stroke={FAINT} strokeWidth="52" strokeLinecap="round" />
            <path d="M 90 214 L 400 214 C 470 213 500 208 522 180 C 540 156 544 120 544 40" fill="none" stroke={RED} strokeWidth="2.5" strokeDasharray="7 5" />
            <circle cx="510" cy="196" r="6" fill={RED} />
            <Label x={500} y={175} anchor="end" fill={RED} weight={600}>apex</Label>
            <Label x={120} y={200} size={10} fill={INK}>enter wide…</Label>
            <Label x={528} y={70} size={10} fill={INK}>…release wide on exit</Label>
            <Label x={80} y={252} size={10} fill={DIM}>the widest arc through the corner carries the most speed onto the straight</Label>
        </Frame>
    );
}

export function Switchback() {
    return (
        <Frame viewBox="0 0 640 260" label="Switchback: defender takes the inside, attacker crosses back for exit speed">
            <path d="M 60 210 L 380 210 C 480 210 540 160 540 60" fill="none" stroke={FAINT} strokeWidth="56" strokeLinecap="round" />
            <path d="M 70 196 L 380 196 C 450 196 480 185 505 155" fill="none" stroke={DIM} strokeWidth="2" strokeDasharray="4 4" />
            <Label x={210} y={186} size={10} fill={DIM}>defender covers inside (slow line)</Label>
            <path d="M 70 222 L 360 222 C 460 224 520 195 528 90" fill="none" stroke={RED} strokeWidth="2.5" strokeDasharray="7 5" />
            <Label x={190} y={246} size={10} fill={RED}>attacker crosses behind → better exit</Label>
            <circle cx="500" cy="160" r="8" fill={PANEL} stroke={INK} />
            <circle cx="524" cy="120" r="8" fill={RED} />
            <path d="M 528 108 L 534 78 m -10 6 l 10 -6 l 2 11" stroke={RED} strokeWidth="1.5" fill="none" />
        </Frame>
    );
}

/* ---------------------------------- Car tech ---------------------------------- */

export function DownforceSketch() {
    return (
        <Frame viewBox="0 0 640 250" label="Downforce: airflow over the car presses it into the track">
            <line x1="40" y1="200" x2="600" y2="200" stroke={DIM} strokeWidth="3" />
            {/* car body */}
            <path d="M 200 176 L 260 150 L 420 146 L 470 168 L 470 184 L 200 184 Z" fill={PANEL} stroke={DIM} />
            <rect x={188} y={142} width={18} height={34} rx={3} fill={PANEL} stroke={DIM} />
            <rect x={452} y={132} width={30} height={10} rx={2} fill={PANEL} stroke={INK} />
            <circle cx="250" cy="190" r="16" fill="#09090b" stroke={DIM} strokeWidth="5" />
            <circle cx="430" cy="190" r="16" fill="#09090b" stroke={DIM} strokeWidth="5" />
            {/* airflow */}
            <path d="M 60 120 C 180 110 240 96 330 92 C 430 88 520 100 600 84" stroke={INK} strokeWidth="1.8" fill="none" opacity="0.7" />
            <path d="M 60 150 C 160 146 200 130 300 126" stroke={INK} strokeWidth="1.8" fill="none" opacity="0.45" />
            <path d="M 210 196 C 300 208 380 208 462 198" stroke={RED} strokeWidth="1.8" fill="none" opacity="0.9" />
            <Label x={330} y={70} anchor="middle" fill={INK}>slower air, higher pressure — pushes down</Label>
            <Label x={336} y={232} anchor="middle" fill={RED}>fast air under the floor, low pressure — sucks down</Label>
            <path d="M 330 100 l 0 30 m -7 -10 l 7 10 l 7 -10" stroke={INK} strokeWidth="2" fill="none" />
        </Frame>
    );
}

export function ErsFlow() {
    return (
        <Frame viewBox="0 0 640 220" label="ERS energy loop: braking charges the battery, deployment boosts the straights">
            <rect x={60} y={80} width={140} height={54} rx={8} fill={PANEL} stroke={INK} />
            <Label x={130} y={103} anchor="middle" fill={INK} weight={600}>BRAKING</Label>
            <Label x={130} y={121} anchor="middle" size={10}>harvest ≤ 2 MJ/lap</Label>
            <rect x={260} y={80} width={130} height={54} rx={8} fill={PANEL} stroke={RED} strokeWidth="1.5" />
            <Label x={325} y={103} anchor="middle" fill={RED} weight={600}>BATTERY</Label>
            <Label x={325} y={121} anchor="middle" size={10}>state of charge</Label>
            <rect x={450} y={80} width={140} height={54} rx={8} fill={PANEL} stroke={INK} />
            <Label x={520} y={103} anchor="middle" fill={INK} weight={600}>DEPLOY</Label>
            <Label x={520} y={121} anchor="middle" size={10}>+160 hp on straights</Label>
            <path d="M 202 107 l 52 0 m -9 -6 l 9 6 l -9 6" stroke={INK} strokeWidth="1.6" fill="none" />
            <path d="M 392 107 l 52 0 m -9 -6 l 9 6 l -9 6" stroke={RED} strokeWidth="1.6" fill="none" />
            <path d="M 520 138 C 520 185 130 185 130 138" stroke={DIM} strokeWidth="1.4" fill="none" strokeDasharray="5 4" />
            <Label x={325} y={196} anchor="middle" size={10} fill={DIM}>…and the next braking zone refills it: a per-lap energy budget</Label>
            <Label x={325} y={40} anchor="middle" fill={DIM}>spend it now or save it — the driver chooses where the boost lands</Label>
        </Frame>
    );
}

/* ---------------------------------- Championship ---------------------------------- */

export function PointsTable() {
    const gp = [25, 18, 15, 12, 10, 8, 6, 4, 2, 1];
    const sprint = [8, 7, 6, 5, 4, 3, 2, 1];
    return (
        <Frame viewBox="0 0 640 230" label="Points by finishing position: Grand Prix and sprint">
            <Label x={40} y={34} fill={INK} weight={600}>GRAND PRIX</Label>
            {gp.map((pts, i) => (
                <g key={`gp${i}`}>
                    <rect x={40 + i * 56} y={80 - pts * 1.7} width={40} height={pts * 1.7} rx={3} fill={i === 0 ? RED : FAINT} />
                    <Label x={60 + i * 56} y={100} anchor="middle" size={10} fill={INK}>{`P${i + 1}`}</Label>
                    <Label x={60 + i * 56} y={72 - pts * 1.7} anchor="middle" size={10} fill={i === 0 ? RED : DIM} weight={600}>{pts}</Label>
                </g>
            ))}
            <Label x={40} y={140} fill={INK} weight={600}>SPRINT</Label>
            {sprint.map((pts, i) => (
                <g key={`s${i}`}>
                    <rect x={40 + i * 56} y={196 - pts * 3.2} width={40} height={pts * 3.2} rx={3} fill={i === 0 ? RED : FAINT} opacity="0.8" />
                    <Label x={60 + i * 56} y={216} anchor="middle" size={10} fill={INK}>{`P${i + 1}`}</Label>
                    <Label x={60 + i * 56} y={188 - pts * 3.2} anchor="middle" size={10} fill={DIM} weight={600}>{pts}</Label>
                </g>
            ))}
        </Frame>
    );
}

export const DIAGRAMS = {
    "weekend-timeline": WeekendTimeline,
    "sprint-timeline": SprintTimeline,
    "qualifying-flow": QualifyingFlow,
    "timing-screen": TimingScreen,
    "flag-cards": FlagCards,
    "safety-car-restart": SafetyCarRestart,
    "drs-zones": DrsZones,
    "compound-ladder": CompoundLadder,
    "deg-curve": DegCurve,
    "undercut-timeline": UndercutTimeline,
    "pit-loss": PitLoss,
    "racing-line": RacingLine,
    switchback: Switchback,
    "downforce-sketch": DownforceSketch,
    "ers-flow": ErsFlow,
    "points-table": PointsTable,
};

export function TopicDiagram({ visual }) {
    const Component = visual?.type ? DIAGRAMS[visual.type] : null;
    if (!Component) return null;
    return (
        <figure className="overflow-hidden rounded-md border border-white/10 bg-black/30 p-4">
            <Component />
        </figure>
    );
}
